/**
 * Ambassador / referral capture for Shopify order note_attributes (cart attributes).
 *
 * Theme files touched:
 *   - layout/theme.liquid — loads this script (defer, after global.js).
 *   - assets/referral-cart-attributes.js — this file.
 *
 * Testing (see PR description “Testing” section):
 *   1. Open any storefront URL with ?ref=test_slug (or affiliate_id / affiliate).
 *   2. Navigate internally (no query string); add a product to cart; open cart drawer or /cart.
 *   3. Complete checkout (guest is fine). In Admin → Order → Additional details, confirm
 *      note attributes include ref (and affiliate_id) with the captured token.
 *   Optional: append &mha_ref_debug=1 to log sync steps to the browser console.
 */
(function () {
  "use strict";

  var SESSION_KEY = "mha_ambassador_ref";
  var COOKIE_NAME = "mha_ambassador_ref";
  var MAX_LEN = 150;
  var COOKIE_DAYS = 14;
  var PARAM_ORDER = ["ref", "affiliate_id", "affiliate"];

  var _syncPromise = null;
  var _debounceTimer = null;

  function isDebug() {
    try {
      return new URLSearchParams(window.location.search).get("mha_ref_debug") === "1";
    } catch (e) {
      return false;
    }
  }

  function dbg() {
    if (!isDebug() || typeof console === "undefined" || !console.log) return;
    var a = Array.prototype.slice.call(arguments);
    a.unshift("[mha-ref]");
    console.log.apply(console, a);
  }

  function sanitizeToken(raw) {
    if (raw == null || typeof raw !== "string") return "";
    var trimmed = raw.trim().slice(0, MAX_LEN);
    var allowed = trimmed.replace(/[^a-zA-Z0-9_-]/g, "");
    return allowed.slice(0, MAX_LEN);
  }

  function readTokenFromSearch(search) {
    try {
      var p = new URLSearchParams(search || "");
      for (var i = 0; i < PARAM_ORDER.length; i++) {
        var k = PARAM_ORDER[i];
        var v = p.get(k);
        if (v) {
          var t = sanitizeToken(v);
          if (t) return t;
        }
      }
    } catch (e) {}
    return "";
  }

  function setCookie(value) {
    if (!value) return;
    var maxAge = COOKIE_DAYS * 24 * 60 * 60;
    var enc = encodeURIComponent(value);
    document.cookie =
      COOKIE_NAME +
      "=" +
      enc +
      ";path=/;max-age=" +
      maxAge +
      ";SameSite=Lax";
  }

  function getCookie() {
    try {
      var parts = ("; " + document.cookie).split("; " + COOKIE_NAME + "=");
      if (parts.length < 2) return "";
      return decodeURIComponent(parts.pop().split(";").shift() || "");
    } catch (e) {
      return "";
    }
  }

  function persistToken(token) {
    if (!token) return;
    try {
      sessionStorage.setItem(SESSION_KEY, token);
    } catch (e) {}
    setCookie(token);
  }

  function captureFromUrl() {
    var fromUrl = readTokenFromSearch(window.location.search);
    if (fromUrl) {
      persistToken(fromUrl);
      dbg("captured from URL", fromUrl);
      return fromUrl;
    }
    return "";
  }

  function getStoredToken() {
    var t = "";
    try {
      t = sessionStorage.getItem(SESSION_KEY) || "";
    } catch (e) {}
    if (t) {
      t = sanitizeToken(t);
      if (t) return t;
    }
    var c = sanitizeToken(getCookie());
    if (c) {
      try {
        sessionStorage.setItem(SESSION_KEY, c);
      } catch (e2) {}
      return c;
    }
    return "";
  }

  function cartJsonUrl() {
    var u = window.routes && window.routes.cart_url;
    if (!u) return null;
    return u.indexOf(".js") !== -1 ? u : u + ".js";
  }

  function updateUrl() {
    var u = window.routes && window.routes.cart_update_url;
    return u || null;
  }

  function attributesNeedUpdate(attrs, token) {
    if (!token) return false;
    var a = attrs || {};
    var r = sanitizeToken(String(a.ref || ""));
    var id = sanitizeToken(String(a.affiliate_id || ""));
    return r !== token || id !== token;
  }

  function mergeAttributes(existing, token) {
    var out = {};
    var k;
    if (existing && typeof existing === "object") {
      for (k in existing) {
        if (Object.prototype.hasOwnProperty.call(existing, k)) {
          out[k] = existing[k];
        }
      }
    }
    out.ref = token;
    out.affiliate_id = token;
    return out;
  }

  function ensureCartAttributesImmediate() {
    var token = getStoredToken();
    if (!token) return Promise.resolve(false);

    var updateU = updateUrl();
    var cartU = cartJsonUrl();
    if (!updateU || !cartU) return Promise.resolve(false);

    if (_syncPromise) return _syncPromise;

    _syncPromise = fetch(cartU, { method: "GET", credentials: "same-origin" })
      .then(function (res) {
        if (!res.ok) throw new Error("cart.js");
        return res.json();
      })
      .then(function (cart) {
        var cur = (cart && cart.attributes) || {};
        if (!attributesNeedUpdate(cur, token)) {
          dbg("cart attributes already set; skip POST");
          return false;
        }
        var body = JSON.stringify({
          attributes: mergeAttributes(cur, token),
        });
        return fetch(updateU, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: body,
        }).then(function (r) {
          if (!r.ok) throw new Error("cart/update");
          dbg("cart attributes updated", { ref: token, affiliate_id: token });
          return true;
        });
      })
      .catch(function (err) {
        dbg("sync error", err);
        return false;
      })
      .finally(function () {
        _syncPromise = null;
      });

    return _syncPromise;
  }

  function scheduleDebouncedSync() {
    if (_debounceTimer) clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(function () {
      _debounceTimer = null;
      void ensureCartAttributesImmediate();
    }, 400);
  }

  function bindCartUpdateListener() {
    if (typeof window.webvista === "undefined") return;
    if (typeof PUB_SUB_EVENTS === "undefined") return;
    try {
      window.webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        scheduleDebouncedSync();
      });
    } catch (e) {}
  }

  function bindCheckoutSubmitGuard() {
    document.addEventListener(
      "submit",
      function (e) {
        var form = e.target;
        if (!form || form.nodeName !== "FORM") return;
        if (!getStoredToken()) return;

        var action = (form.getAttribute("action") || "").toLowerCase();
        if (action.indexOf("/cart") === -1) return;

        var sub = e.submitter;
        if (!sub || sub.getAttribute("name") !== "checkout") return;
        if (form.getAttribute("data-mha-referral-checkout-synced") === "true") {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        ensureCartAttributesImmediate()
          .then(function () {
            form.setAttribute("data-mha-referral-checkout-synced", "true");
            try {
              if (typeof form.requestSubmit === "function") {
                form.requestSubmit(sub);
              } else {
                form.submit();
              }
            } finally {
              form.removeAttribute("data-mha-referral-checkout-synced");
            }
          })
          .catch(function () {
            try {
              if (typeof form.requestSubmit === "function") {
                form.requestSubmit(sub);
              } else {
                form.submit();
              }
            } catch (err2) {}
          });
      },
      true,
    );
  }

  /** Best-effort before Shop Pay / accelerated checkout handles the event. */
  function bindAcceleratedCheckoutPrefetch() {
    document.addEventListener(
      "pointerdown",
      function (e) {
        if (!getStoredToken()) return;
        var el = e.target;
        if (!el || !el.closest) return;
        if (
          el.closest(".shopify-payment-button") ||
          el.closest("shopify-accelerated-checkout")
        ) {
          void ensureCartAttributesImmediate();
        }
      },
      true,
    );
  }

  function boot() {
    captureFromUrl();
    bindCartUpdateListener();
    var t = getStoredToken();
    if (t) {
      void ensureCartAttributesImmediate();
      scheduleDebouncedSync();
    }
    bindCheckoutSubmitGuard();
    bindAcceleratedCheckoutPrefetch();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.addEventListener("popstate", function () {
    captureFromUrl();
    if (getStoredToken()) scheduleDebouncedSync();
  });

  window.addEventListener("pageshow", function () {
    captureFromUrl();
    if (getStoredToken()) scheduleDebouncedSync();
  });

  window.MHA_SYNC_REFERRAL_CART_ATTRIBUTES = ensureCartAttributesImmediate;
})();
